import {
	createRef,
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useState
} from 'react';
import { Badge, Divider } from '@material-ui/core';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import CreateIcon from '@material-ui/icons/Create';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	MapViewerPanel,
	AppState,
	mapViewerSwitchActionPanel,
	Project,
	AppThunkDispatch,
	MapViewerPanelAction
} from '../../../redux';
import { LeafletBottomLeft } from '../../../utils';
import {
	DragHandle,
	FreeDraggable,
	MapControl,
	MapControlContainer,
	TooltipIconButton
} from '../../atoms';
import PinDrop from '@material-ui/icons/PinDrop';
import { ConfirmationDialog, IConfirmationDialogProps } from '..';

import './map-actions-panel.styles.scss';
import { AnalyticsElementModule } from '../../../models';
import { useTranslation } from 'react-i18next';
import { SaveDialog } from '../save-dialog/save-dialog.component';
import { useTracker } from '../../../hooks/useTracker';

export interface IMapActionOptions {
	googleMaps?: boolean;
	comparisons?: boolean;
	drawing?: boolean;
	attachments?: boolean;
	sharedProject?: boolean;
}

export interface IMapActionsPanelOwnProps {
	attachmentCount: number;
	options?: IMapActionOptions;
	mapId?: string;
	projectTitle?: string;
}

export interface IMapActionsPanelStateProps {
	isProjectDirty: boolean;
	panels: Map<MapViewerPanel, boolean>;
	project?: Project;
	togglePanel: (panel: MapViewerPanel) => void;
}

function MapActionsPanelView(
	props: IMapActionsPanelOwnProps & IMapActionsPanelStateProps
): ReactElement {
	const {
		mapId,
		panels,
		options,
		attachmentCount,
		isProjectDirty,
		togglePanel,
		projectTitle
	} = props;
	const tracker = useTracker()
	const { t } = useTranslation(['general', 'map', 'project']);

	const dragHandleRef: RefObject<HTMLDivElement> =
		createRef<HTMLDivElement>();

	const [confirmDialogProps, setConfirmDialogProps] =
		useState<IConfirmationDialogProps>({
			open: false,
			title: t('map:actionButtons.comparison.makeComparison'),
			bodyText: (
				<>
					<b>
						{t(
							'map:actionButtons.comparison.notYetSavedComparison'
						)}
					</b>
					<br />
					{t(
						'map:actionButtons.comparison.notYetSavedComparisonText'
					)}
				</>
			),
			onCancel: closeConfirmationDialog,
			onConfirm: () => {},
			confirmText: t('general:save')
		});

	const [showWarning, setShowWarning] = useState<boolean>(false);
	const [activePanel, setActivePanel] = useState<MapViewerPanel>();
	const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);

	const showAttachmentBadge: boolean =
		attachmentCount > 0 &&
		!(panels.get(MapViewerPanel.Attachement) || false);

	function optionalToBoolean(option?: boolean): boolean {
		return option === undefined ? true : option;
	}

	const history = useHistory();

	const showGoolgeMaps: boolean = optionalToBoolean(options?.googleMaps);
	const showComparisons: boolean = optionalToBoolean(options?.comparisons);
	const showDrawing: boolean = optionalToBoolean(options?.drawing);
	const showAttachments: boolean = optionalToBoolean(options?.attachments);
	const sharedProject: boolean = optionalToBoolean(options?.sharedProject);

	const googleMapsActive: boolean | undefined = useMemo(() => {
		return panels.get(MapViewerPanel.GoogleMaps);
	}, [panels]);

	function handleComparisonClicked(): void {
		trackPanelItemClicked('comparison');
		if (isProjectDirty) {
			setConfirmDialogProps({
				...confirmDialogProps,
				open: true,
				onConfirm: () => {
					handleSave();
				}
			});
		} else {
			history.replace(`/map/${mapId}/comparison`);
		}
	}

	function trackPanelItemClicked(itemId: string): void {
		tracker.elementClicked({
			module: AnalyticsElementModule.MapActionsPanel,
			itemId,
			type: 'button',
			elementId: 'map-action'
		});
	}

	function handlePanelToggleClick(panel?: MapViewerPanel): void {
		if (panel === undefined) return;
		setActivePanel(panel);

		if (
			sharedProject &&
			showWarning === false &&
			(panel === MapViewerPanel.Attachement ||
				panel === MapViewerPanel.Drawing)
		) {
			setShowWarning(true);
			return;
		}
		trackPanelItemClicked(getPanelName(panel));
		togglePanel(panel);
	}

	function getPanelName(panel: MapViewerPanel): string {
		switch (panel) {
			case MapViewerPanel.Attachement:
				return 'attachment';
			case MapViewerPanel.GoogleMaps:
				return 'google-maps';
			case MapViewerPanel.Drawing:
				return 'drawing';
			default:
				throw new Error('Unknown panel');
		}
	}

	function closeConfirmationDialog(): void {
		setConfirmDialogProps({ ...confirmDialogProps, open: false });
	}

	function handleSave(): void {
		setShowSaveDialog(true);
		closeConfirmationDialog();
	}

	useEffect(() => {
		if (!showWarning) return;

		setConfirmDialogProps({
			...confirmDialogProps,
			open: true,
			title: t('project:warningEditSharedMap.warningSharedMap'),
			bodyText: <>{t('project:warningEditSharedMap.warningEditSave')}</>,
			onConfirm: () => {
				closeConfirmationDialog();
				setShowWarning(false);
				handlePanelToggleClick(activePanel);
			},
			onCancel: () => {
				closeConfirmationDialog();
				setShowWarning(false);
			}
		});
	}, [showWarning]);

	return (
		<>
			<ConfirmationDialog {...confirmDialogProps} />
			{showSaveDialog && (
				<SaveDialog
					mapId={mapId}
					projectTitle={projectTitle}
					closeSaveDialog={() => setShowSaveDialog(false)}
				/>
			)}
			<MapControlContainer
				position={LeafletBottomLeft}
				className="map-actions-panel"
			>
				<FreeDraggable handleRef={dragHandleRef}>
					<MapControl>
						<DragHandle ref={dragHandleRef} />
						<Divider orientation="vertical" />
						{showGoolgeMaps && (
							<TooltipIconButton
								onClick={() =>
									handlePanelToggleClick(
										MapViewerPanel.GoogleMaps
									)
								}
								selected={googleMapsActive}
								placement="top"
								tooltip="Google Maps"
								icon={<PinDrop />}
							/>
						)}
						{showComparisons && (
							<TooltipIconButton
								onClick={handleComparisonClicked}
								disabled={googleMapsActive}
								placement="top"
								tooltip={t('map:actionButtons.compare')}
								icon={<CompareArrowsIcon />}
							/>
						)}

						{showDrawing && (
							<TooltipIconButton
								onClick={() =>
									handlePanelToggleClick(
										MapViewerPanel.Drawing
									)
								}
								disabled={googleMapsActive}
								selected={panels.get(MapViewerPanel.Drawing)}
								placement="top"
								tooltip={t('map:actionButtons.edit.tooltip')}
								icon={<CreateIcon />}
							/>
						)}
						{showAttachments && (
							<TooltipIconButton
								onClick={() =>
									handlePanelToggleClick(
										MapViewerPanel.Attachement
									)
								}
								selected={panels.get(
									MapViewerPanel.Attachement
								)}
								disabled={googleMapsActive}
								placement="top"
								tooltip={t(
									'map:actionButtons.attachmentTooltip'
								)}
								icon={
									<Badge
										badgeContent={
											showAttachmentBadge
												? attachmentCount
												: undefined
										}
										color="secondary"
									>
										<AttachFileIcon />
									</Badge>
								}
							/>
						)}
					</MapControl>
				</FreeDraggable>
			</MapControlContainer>
		</>
	);
}

function mapStateToProps(state: AppState) {
	return {
		panels: state.mapViewer.panels,
		isProjectDirty: state.project.present.isDirty
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MapViewerPanelAction>) {
	return {
		togglePanel: (panel: MapViewerPanel) =>
			dispatch(mapViewerSwitchActionPanel(panel))
	};
}

export const MapActionsPanel = connect(
	mapStateToProps,
	mapDispatchToProps
)(MapActionsPanelView);
